// developers（开发者接入指南）页面私有文案：zh 为完整基准，en 逐 key 对齐
// 飞书完整文档外链地址见 DevelopersClient 中的 FEISHU_DOC_URL 常量（改版方案 §5.6）
export interface DevelopersDict {
    meta: { title: string; description: string }
    hero: {
        badge: string
        titleA: string
        titleB: string
        subtitle: string
    }
    steps: {
        titleA: string
        titleB: string
        items: { title: string; desc: string }[]
    }
    capabilities: {
        titleA: string
        titleB: string
        items: { label: string; value: string }[]
    }
    faq: {
        title: string
        desc: string
        items: { q: string; a: string }[]
    }
    docs: {
        title: string
        desc: string
        button: string
    }
    cta: {
        title: string
        button: string
    }
}

export const developersDict: { zh: DevelopersDict; en: DevelopersDict } = {
    zh: {
        meta: {
            title: '开发者接入指南 - YYGame',
            description: 'YYGame 互动游戏接入指南：商务洽谈、接入调试、上线运营三步完成接入，SDK 支持 Android / iOS / Web，多语言开箱即用，最快 3 天上线。',
        },
        hero: {
            badge: 'Developers',
            titleA: '三步接入，',
            titleB: '让互动玩法快速上线',
            subtitle: '全流程专人陪跑，最快 3 天接入',
        },
        steps: {
            titleA: '清晰三步，',
            titleB: '快速上线',
            items: [
                { title: '商务洽谈', desc: '明确合作模式与内容组合，签署协议并开通接入权限与演示环境。' },
                { title: '接入调试', desc: '按文档对接 SDK / API，技术团队全程陪跑联调，标准接入最快 3 天完成。' },
                { title: '上线运营', desc: '灰度验证后全量上线，配套运营玩法与数据复盘，持续优化活跃与营收。' },
            ],
        },
        capabilities: {
            titleA: '能力清单，',
            titleB: '开箱即用',
            items: [
                { label: 'SDK 版本', value: 'v3.x 稳定版' },
                { label: '接入时长', value: '最快 3 天' },
                { label: '支持端', value: 'Android · iOS · Web' },
                { label: '多语言', value: '简中 / 英语 / 日语等' },
            ],
        },
        faq: {
            title: '常见问题',
            desc: '接入前大家最关心的几个问题',
            items: [
                { q: '接入需要多少开发人力？', a: '标准 H5 / SDK 接入 1-2 名客户端工程师即可，文档与 Demo 齐全；联调期间我们提供一对一技术支持，通常 3 天内完成。' },
                { q: '支持哪些端与系统版本？', a: '支持 Android、iOS 原生 SDK 与 Web（H5）接入，覆盖主流系统版本；Web 端可直接以 iframe / 链接形式嵌入，最快当天上线。' },
                { q: '游戏内容支持哪些语言？', a: '默认支持简体中文与英语，日语等多语言可按需开启；如需小语种，联系商务即可快速扩展。' },
                { q: '接入后如何获得运营支持？', a: '上线后提供运营玩法方案、版本更新与数据复盘支持，7×24 技术响应，重大活动可提前申请专项保障。' },
            ],
        },
        docs: {
            title: '查看完整接入文档',
            desc: 'SDK 下载、接口说明、Demo 与更新日志均在飞书文档中维护，实时更新。',
            button: '打开飞书文档',
        },
        cta: {
            title: '准备好开始接入了吗？',
            button: '联系商务',
        },
    },
    en: {
        meta: {
            title: 'Developer Guide - YYGame',
            description: 'YYGame integration guide: business talk, integration & debugging, then launch — go live in as fast as 3 days with SDKs for Android, iOS and Web, plus multi-language support out of the box.',
        },
        hero: {
            badge: 'Developers',
            titleA: 'Three steps to ',
            titleB: 'go live fast',
            subtitle: 'Dedicated support end-to-end — live in as fast as 3 days',
        },
        steps: {
            titleA: 'A clear ',
            titleB: 'three-step flow',
            items: [
                { title: 'Business Talk', desc: 'Align on the partnership model and content mix, sign the agreement, and get integration access plus a demo environment.' },
                { title: 'Integration & Debugging', desc: 'Integrate via SDK / API following the docs, with our engineers pairing through testing — standard integration takes as fast as 3 days.' },
                { title: 'Launch & Operate', desc: 'Roll out gradually, then go full scale with operation playstyles and data reviews to keep improving engagement and revenue.' },
            ],
        },
        capabilities: {
            titleA: 'Capability checklist, ',
            titleB: 'out of the box',
            items: [
                { label: 'SDK Version', value: 'v3.x Stable' },
                { label: 'Integration Time', value: 'As fast as 3 days' },
                { label: 'Platforms', value: 'Android · iOS · Web' },
                { label: 'Languages', value: 'ZH / EN / JA & more' },
            ],
        },
        faq: {
            title: 'FAQ',
            desc: 'Common questions before integration',
            items: [
                { q: 'How many engineers do we need?', a: 'A standard H5 / SDK integration takes 1-2 client engineers. Docs and demos are ready, and our team provides one-on-one support — usually done within 3 days.' },
                { q: 'Which platforms and OS versions are supported?', a: 'Native SDKs for Android and iOS plus Web (H5), covering mainstream OS versions. The Web build can be embedded via iframe or link and go live the same day.' },
                { q: 'Which languages do the games support?', a: 'Simplified Chinese and English by default, with Japanese and more available on demand — contact sales for additional languages.' },
                { q: 'What support do we get after launch?', a: 'Operation playbooks, version updates and data reviews, with 7×24 technical response and dedicated support for major campaigns.' },
            ],
        },
        docs: {
            title: 'Read the full documentation',
            desc: 'SDK downloads, API references, demos and changelogs are all maintained in the Feishu doc, always up to date.',
            button: 'Open Feishu Doc',
        },
        cta: {
            title: 'Ready to start integrating?',
            button: 'Contact Sales',
        },
    },
}
